import { can as canDo, type Permission, type Role } from '@console/shared'
import { QueryClient, useQuery } from '@tanstack/react-query'

export class HttpError extends Error {
  constructor(
    public status: number,
    public type: string,
    message: string,
    public details?: string[],
  ) {
    super(message)
  }
}

export async function api<T = unknown>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const r = await fetch(path, {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers: init.body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  })
  const text = await r.text()
  const json = text ? (JSON.parse(text) as unknown) : null
  if (!r.ok) {
    const e = (json as { error?: { type?: string; message?: string; details?: string[] } } | null)?.error
    throw new HttpError(r.status, e?.type ?? 'error', e?.message ?? `Request failed (${r.status})`, e?.details)
  }
  return json as T
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, err) => !(err instanceof HttpError && err.status < 500) && n < 2,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
})

export interface Org {
  id: string
  name: string
  slug: string
  role: Role
}
export interface PendingInvitation {
  id: string
  role: string | null
  orgId: string
  orgName: string
  expiresAt: string
}
export interface Me {
  user: { id: string; email: string; name: string; image?: string | null; emailVerified: boolean } | null
  isAdmin?: boolean
  /** The admin app's address (admins only). */
  adminUrl?: string
  /** granted: may use the console; pending/rejected: invite-only beta, waiting on (or refused) an access request. */
  access?: 'granted' | 'pending' | 'rejected'
  accessRequest?: AccessRequest
  /** May start organizations of their own (approved, admin, or open sign-up); invitees work in the one they joined. */
  canCreateOrg?: boolean
  activeOrgId?: string | null
  orgs?: Org[]
  pendingInvitations?: PendingInvitation[]
}
export interface AccessRequest {
  company: string | null
  useCase: string | null
  createdAt: string
}
export interface AppConfig {
  features: { google: boolean; billing: boolean; email: boolean }
  signupMode: 'invite_only' | 'open'
  topup: { minUsd: number; maxUsd: number }
  publicApiUrl: string
  /** The public docs site (docs.trodo.ai/console); null means the guide built into the console at /docs. */
  docsUrl: string | null
}

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/me') })
export const useConfig = () =>
  useQuery({ queryKey: ['config'], queryFn: () => api<AppConfig>('/api/config'), staleTime: 300_000 })

export function useActiveOrg() {
  const { data } = useMe()
  const org = data?.orgs?.find((o) => o.id === data.activeOrgId) ?? null
  const role = org?.role ?? null
  return {
    org,
    role,
    /** What the signed-in person may do in the active organisation (same matrix the API enforces). */
    can: (p: Permission) => Boolean(role && canDo(role, p)),
    canManage: Boolean(role && canDo(role, 'members.manage')),
  }
}

export interface ModelInfo {
  id: string
  displayName: string
  family: string
  description: string
  status: string
  mock: boolean
  isDefault: boolean
  aliases: string[]
  contextTokens: number
  usdPerMillionInputTokens: number
}
export const useModels = () =>
  useQuery({ queryKey: ['models'], queryFn: () => api<{ data: ModelInfo[] }>('/api/models'), staleTime: 60_000 })

export interface Billing {
  balanceNano: number
  status: 'active' | 'suspended'
  autoTopup: { enabled: boolean; thresholdNano: number; amountNano: number; hasPaymentMethod: boolean }
  ledger: { id: string; amountNano: number; balanceAfterNano: number; kind: string; note: string | null; reference: string | null; createdAt: string }[]
  topups: { id: string; kind: string; amountNano: number; status: string; createdAt: string }[]
  billingEnabled: boolean
  canManage: boolean
}
export const useBilling = (enabled = true) =>
  useQuery({ queryKey: ['billing'], queryFn: () => api<Billing>('/api/billing'), enabled })

/** Ask the admins for an organization of your own (someone who came in through an invitation). */
export async function requestOwnOrganization(): Promise<void> {
  await api('/api/access-request', { body: {} })
}
